import React, {
  CSSProperties,
  InputHTMLAttributes,
  ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react';
import styles from './CLInput.module.scss';
import classNames from 'classnames';
import CLLabel from '../CLLabel/CLLabel';
import CLMaskIcon from 'common/src/components/CLMaskIcon/CLMaskIcon';
import iconsObj from '../../assets/icons/iconsObj';
import pluralize from 'pluralize';
import _isNil from 'lodash/isNil';
import addAutomationTag from 'common/src/modules/automation/utils/addAutomationTag';
import {AutomationProps} from 'common/src/modules/automation/automation.types';

export type CLInputSize = 'small' | 'medium' | 'full';

interface CLInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'>,
    AutomationProps {
  name?: string;
  fullWidth?: boolean;
  containerClassName?: string;
  inputClassName?: string;
  className?: string;
  dimension?: CLInputSize;
  adjustSize?: boolean;
  label?: ReactNode;
  icon?: string;
  iconClassName?: string;
  iconWrapperClassName?: string;
  onChange?: (value: string, inputType?: string) => void;
  onIconClick?: () => void;
  suffix?: string;
  suffixClassName?: string;
}

const CLInput = React.forwardRef<HTMLInputElement, CLInputProps>(
  (
    {
      name,
      fullWidth = false,
      className = '',
      containerClassName = '',
      inputClassName = containerClassName || '',
      dimension = 'medium',
      value,
      adjustSize = false,
      onChange = () => {},
      label,
      required = false,
      icon = '',
      iconClassName = '',
      disabled,
      iconWrapperClassName = '',
      onIconClick,
      onBlur,
      suffix,
      suffixClassName = '',
      type,
      qaHook = '',
      ...other
    }: CLInputProps,
    ref,
  ) => {
    const id = useId();
    const localRef = useRef(null);
    const usedRef = ref || localRef;

    useEffect(
      function disableScrollingOnActiveNumberInputsWheel() {
        const currentElement = typeof usedRef === 'function' ? undefined : usedRef?.current;

        const handleWheel = (event: WheelEvent) => {
          if (document.activeElement === currentElement) {
            event.preventDefault();
            const direction = event.deltaY < 0 ? 1 : -1;
            let currentValue = String(parseInt(currentElement?.value || '0') + direction);

            if (!_isNil(other.min) && +currentValue < +other.min) currentValue = String(+other.min);
            if (!_isNil(other.max) && +currentValue > +other.max) currentValue = String(+other.max);

            onChange(currentValue);
          }
        };

        !!currentElement &&
          currentElement.type === 'number' &&
          currentElement.addEventListener('wheel', handleWheel);

        return () => {
          !!currentElement &&
            currentElement.type === 'number' &&
            currentElement.removeEventListener('wheel', handleWheel);
        };
      },
      [onChange, other.max, other.min, usedRef],
    );

    return (
      <div className={classNames(styles.container, containerClassName)}>
        {label && (
          <CLLabel withMandatoryMark={required} className={styles.input_label}>
            {label}
          </CLLabel>
        )}
        <div
          className={classNames(styles.common_cl_input, inputClassName, styles[dimension], {
            [styles.disabled]: disabled,
          })}
          style={{'--clear_icon': `url(${iconsObj.clear}`} as CSSProperties}
        >
          <input
            {...addAutomationTag(qaHook)}
            disabled={disabled}
            onBlur={(e) => {
              onBlur && onBlur(e);
            }}
            size={adjustSize ? value?.toString().length : undefined}
            style={{width: adjustSize ? 'auto' : `100%`}}
            ref={usedRef}
            name={name}
            autoComplete="off"
            value={value}
            onChange={(event) => {
              let value = event.target.value;
              if (!_isNil(other.min) && +value < +other.min) value = String(+other.min);
              if (!_isNil(other.max) && +value > +other.max) value = String(+other.max);

              onChange?.(value, (event.nativeEvent as InputEvent).inputType);
            }}
            {...other}
            className={classNames(
              styles.input,
              {[styles.cl_fit_width]: fullWidth, [styles.cl_input_with_icon]: !!icon},
              className,
            )}
            type={type}
            id={id}
          />
          {suffix && value && (
            <div className={suffixClassName} style={{left: `${20 + String(value).length * 7}px`}}>
              {type === 'number' ? pluralize(suffix, +value) : suffix}
            </div>
          )}
          {!!icon && (
            <label
              htmlFor={id}
              className={classNames(styles.cl_input_icon, iconWrapperClassName)}
              onClick={onIconClick}
            >
              <CLMaskIcon src={icon} className={iconClassName} />
            </label>
          )}
        </div>
      </div>
    );
  },
);

export default CLInput;

export {CLInputProps};
